import {ReviewSummary} from 'constants/SourceConstants';
import {QueryResultType, SkippableBenchlingQueryResult} from 'graphql-client/BenchlingQuery';

import {HomepageAssignedProcessTasksQuery} from '../__generated/HomepageAssignedProcessTasksQuery';
import {
  REVIEW_STATUS_LABEL_TO_SUMMARY_KEY,
  REVIEW_SUMMARY_COUNT_KEYS,
  TASK_STATUS_LABEL_TO_SUMMARY_KEY,
  TASK_STATUS_TYPE_TO_SUMMARY_KEY,
  TasksReviewSummaryCountKey,
} from './MyWorkConstants';

export type TasksReviewSummary = Record<TasksReviewSummaryCountKey, number> & {
  ids: Record<TasksReviewSummaryCountKey, Array<string>>;
};

function createEmptyTasksReviewSummary(): TasksReviewSummary {
  return {
    cancelled: 0,
    completed: 0,
    failed: 0,
    invalid: 0,
    inProgress: 0,
    pending: 0,
    skipped: 0,
    ids: {
      cancelled: [],
      completed: [],
      failed: [],
      invalid: [],
      inProgress: [],
      pending: [],
      skipped: [],
    },
  };
}

function sumReviewSummaryCounts(summary: ReviewSummary | null): number | 'Loading' {
  if (summary == null) {
    return 'Loading';
  }
  return REVIEW_SUMMARY_COUNT_KEYS.reduce((acc, key) => acc + summary[key], 0);
}

export function getSelectedReviewSummaryIds(
  summary: ReviewSummary | null,
  selectedStatusLabels: Array<string>
): Array<string> | null {
  // null until the entry/worksheet review summary pre-query has loaded.
  if (summary == null) {
    return null;
  }
  return selectedStatusLabels.flatMap((statusLabel) => {
    const summaryKey = REVIEW_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel] as keyof ReviewSummary['ids'];
    return summary.ids[summaryKey];
  });
}

export function makeTasksReviewSummary(
  tasksQueryResult: SkippableBenchlingQueryResult<HomepageAssignedProcessTasksQuery>
): TasksReviewSummary | null {
  if (tasksQueryResult.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const summary = createEmptyTasksReviewSummary();
  for (const curr of tasksQueryResult.data.assignedProcessTaskStatusSummaries) {
    const statusKey = TASK_STATUS_TYPE_TO_SUMMARY_KEY[curr.status.statusType];
    summary[statusKey] = curr.count;
    summary.ids[statusKey] = curr.taskIds;
  }
  return summary;
}

export function getSelectedTaskIds(
  tasksReviewSummary: TasksReviewSummary | null,
  selectedStatusLabels: Array<string>
): Array<string> | null {
  // null until the assigned process task summary pre-query has loaded.
  if (tasksReviewSummary == null) {
    return null;
  }
  return selectedStatusLabels.flatMap(
    (statusLabel) => tasksReviewSummary.ids[TASK_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel]]
  );
}

function getTasksCount(
  tasksQueryResult: SkippableBenchlingQueryResult<HomepageAssignedProcessTasksQuery>
): number | 'Loading' {
  if (tasksQueryResult.type === QueryResultType.SKIPPED) {
    return 0;
  }
  if (tasksQueryResult.type !== QueryResultType.SUCCESS) {
    return 'Loading';
  }
  return tasksQueryResult.data.assignedProcessTaskStatusSummaries.reduce((acc, curr) => acc + curr.count, 0);
}

export function getCategoryCounts({
  entryReviewSummary,
  worksheetReviewSummary,
  tasksQueryResult,
  showEntriesCategory = true,
  showWorksheetsCategory = true,
  showTasksCategory = true,
}: {
  entryReviewSummary: ReviewSummary | null;
  worksheetReviewSummary: ReviewSummary | null;
  tasksQueryResult: SkippableBenchlingQueryResult<HomepageAssignedProcessTasksQuery>;
  showEntriesCategory?: boolean;
  showWorksheetsCategory?: boolean;
  showTasksCategory?: boolean;
}): {entriesCount: number | 'Loading'; worksheetsCount: number | 'Loading'; tasksCount: number | 'Loading'} {
  return {
    entriesCount: showEntriesCategory ? sumReviewSummaryCounts(entryReviewSummary) : 0,
    worksheetsCount: showWorksheetsCategory ? sumReviewSummaryCounts(worksheetReviewSummary) : 0,
    tasksCount: showTasksCategory ? getTasksCount(tasksQueryResult) : 0,
  };
}
